import { useEffect, useRef } from "react";
import { gsap, prefersReducedMotion } from "@/lib/gsapSetup";

/**
 * DOM / GSAP entrance for Hero copy.
 * Keeps editorial timelines here — do not duplicate with Framer or R3F loops.
 * Future: expose timeline handles for coordinating camera focus / CTA beats.
 */
export default function useHeroAnimation(rootRef, { ready = false, bgRef = null } = {}) {
  const timelineRef = useRef(null);

  useEffect(() => {
    const root = rootRef?.current;
    if (!ready || !root) return undefined;

    const reduced = prefersReducedMotion();
    const ctx = gsap.context(() => {
      const lines = root.querySelectorAll("[data-hero-line]");
      gsap.set(lines, { yPercent: reduced ? 0 : 100 });

      const tl = gsap.timeline({ delay: 0.1 });
      timelineRef.current = tl;

      tl.to(lines, {
        yPercent: 0,
        duration: reduced ? 0.6 : 1.15,
        ease: "power4.out",
        stagger: 0.12,
      });
      tl.from(
        "[data-hero-fade]",
        { opacity: 0, y: 24, duration: 0.9, ease: "power3.out", stagger: 0.12 },
        reduced ? "-=0.2" : "-=0.6"
      );

      if (!reduced && bgRef?.current) {
        gsap.to(bgRef.current, {
          yPercent: 18,
          ease: "none",
          scrollTrigger: {
            trigger: root,
            start: "top top",
            end: "bottom top",
            scrub: true,
          },
        });
      }
    }, root);

    return () => {
      timelineRef.current = null;
      ctx.revert();
    };
  }, [ready, rootRef, bgRef]);

  return { timelineRef };
}
